import { useWindowOpen } from '@repo/platform';
import { useRouter } from 'next/router';
import { useState, useMemo, useCallback } from 'react';

import { getCorporationRefundData } from '@/components/tax-refund/common/data/corporation-refund-data';
import { getCorpsJointCertificateErrorContent } from '@/components/tax-refund/common/utils/getJointCertificateErrorContent';
import JointCertificateSelectView from '@/components/tax-refund/common/view/JointCertificateSelectView';
import { 홈택스공동인증서등록가이드주소, 홈택스아이디생성주소 } from '@/lib/constants/link';
import { CORPORATION_PATHS } from '@/lib/constants/paths';
import { useCheckHometaxAccount } from '@/lib/hooks/api/mutation/use-check-hometax-account';

const CorporationSelectJointCertificateContent = () => {
  const router = useRouter();
  const { openWindow } = useWindowOpen();
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false);
  const [errorIsOpenSelectAfterHandlePrimaryButton, setErrorIsOpenSelectAfterHandlePrimaryButton] = useState(false);
  const [errorDialogContent, setErrorDialogContent] = useState<ReturnType<typeof getCorpsJointCertificateErrorContent> | null>(null);
  const { handleCheckHometaxAccount, isInFlight: isCheckJoinCertiLoading } = useCheckHometaxAccount();
  const corporationRefundData = getCorporationRefundData();

  const loginHometax = useCallback(
    (data: { signCert: string; signPri: string; signPw: string }) => {
      handleCheckHometaxAccount({
        variables: {
          corpType: 'CORPS',
          loginType: 'CommonCert',
          certificate: {
            signCert: data.signCert,
            signPri: data.signPri,
            signPw: data.signPw
          },
          tin: corporationRefundData?.tin
        },
        errorAction: (errorType) => {
          setErrorDialogContent(getCorpsJointCertificateErrorContent(errorType));
          setIsOpenErrorDialog(true);
        }
      });
    },
    [handleCheckHometaxAccount, corporationRefundData]
  );

  const moveJointCertificateInstallPage = useCallback(() => {
    router.push({
      pathname: `${CORPORATION_PATHS.법인공동인증서프로그램설치판단}`,
      query: router.query
    });
  }, [router]);

  const errorHandleClickPrimaryButton = useCallback(() => {
    switch (errorDialogContent?.errorCode) {
      case 'ERR_HOMETAX_NO_CORPS_ID':
      case 'ERR_HOMETAX_EXPIRE_CERT':
      case 'ERR_EXIST_SEARCH_HISTORY':
      case 'ERR_NO_EQUAL_TIN':
        setIsOpenErrorDialog(false);
        setErrorIsOpenSelectAfterHandlePrimaryButton(true);
        break;
      case 'ERR_HOMETAX_NO_REGIST_CERT':
        openWindow(홈택스공동인증서등록가이드주소);
        break;
      case 'ERR_HOMETAX_NO_USER':
        openWindow(홈택스아이디생성주소);
        break;
      default:
        setIsOpenErrorDialog(false);
        break;
    }
  }, [errorDialogContent?.errorCode, openWindow]);

  const errorHandleClickSecondaryButton = useCallback(() => {
    setIsOpenErrorDialog(false);
  }, []);

  const viewController = useMemo(
    () => ({
      isCheckJoinCertiLoading: isCheckJoinCertiLoading,
      loginHometax,
      isOpenErrorDialog,
      moveJointCertificateInstallPage,
      errorTitle: errorDialogContent?.title ?? '',
      errorMessage: errorDialogContent?.message ?? '',
      errorPrimaryButton: errorDialogContent?.primaryButtonText ?? '',
      errorSecondaryButton: errorDialogContent?.secondaryButtonText,
      errorIsOpenSelectAfterHandlePrimaryButton,
      errorHandleClickPrimaryButton,
      errorHandleClickSecondaryButton
    }),
    [
      isCheckJoinCertiLoading,
      loginHometax,
      isOpenErrorDialog,
      moveJointCertificateInstallPage,
      errorDialogContent,
      errorIsOpenSelectAfterHandlePrimaryButton,
      errorHandleClickPrimaryButton,
      errorHandleClickSecondaryButton
    ]
  );

  return <JointCertificateSelectView viewController={viewController} />;
};

export default CorporationSelectJointCertificateContent;
